import {
    AliceAgentAppAdmin,
    AliceAgentAppDTO,
    AliceAgentAppListItem,
    CreateAliceAgentAppRequest,
    CreateAliceAgentAppResponse,
    McpObserveRequest,
    McpObserveResponse,
    UpdateAliceAgentAppRequest,
} from 'client/model/aliceAgentApp';
import { UserAgreement, UserAgreementOrDraft } from 'client/model/userAgreement';

import { routes } from '../../lib/routes';
import { callApi } from '../utils';

const basePath = (skillId?: string) => `/v3/alice-agent-apps${skillId ? `/${skillId}` : ''}`;

export const createAliceAgentApp = (request: CreateAliceAgentAppRequest) =>
    callApi<CreateAliceAgentAppResponse>({
        method: 'POST',
        url: routes.app.appStoreApi(basePath()),
        data: request,
    });

export const listAliceAgentApps = (listAll?: boolean) =>
    callApi<AliceAgentAppListItem[]>({
        method: 'GET',
        url: routes.app.appStoreApi(basePath()),
        queryParams: listAll ? { listAll: true } : undefined,
    });

export const getAliceAgentApp = (skillId: string) =>
    callApi<AliceAgentAppDTO>({
        method: 'GET',
        url: routes.app.appStoreApi(basePath(skillId)),
    });

export const listAliceAgentAppAdmins = (skillId: string) =>
    callApi<AliceAgentAppAdmin[]>({
        method: 'GET',
        url: routes.app.appStoreApi(`${basePath(skillId)}/admins`),
    });

export const addAliceAgentAppAdmin = (skillId: string, login: string) =>
    callApi<AliceAgentAppAdmin>({
        method: 'POST',
        url: routes.app.appStoreApi(`${basePath(skillId)}/admins`),
        data: { login },
    });

export const removeAliceAgentAppAdmin = (skillId: string, userId: string) =>
    callApi<void>({
        method: 'DELETE',
        url: routes.app.appStoreApi(`${basePath(skillId)}/admins/${encodeURIComponent(userId)}`),
    });

export const updateAliceAgentApp = (skillId: string, request: UpdateAliceAgentAppRequest) =>
    callApi<void>({
        method: 'PATCH',
        url: routes.app.appStoreApi(basePath(skillId)),
        data: request,
    });

/**
 * Admin-only override of the MCP server connection timeout. Patches both the draft and the
 * published snapshot immediately (no republication needed). `null` resets to the agent default.
 */
export const updateAliceAgentAppMcpTimeout = (skillId: string, timeoutSeconds: number | null) =>
    callApi<void>({
        method: 'PATCH',
        url: routes.app.appStoreApi(`/admin/alice-agent-app/${skillId}/mcp-timeout`),
        data: { timeoutSeconds },
    });

export const publishAliceAgentApp = (skillId: string) =>
    callApi<void>({
        method: 'POST',
        url: routes.app.appStoreApi(`${basePath(skillId)}/publish`),
    });

export const unpublishAliceAgentApp = (skillId: string) =>
    callApi<void>({
        method: 'POST',
        url: routes.app.appStoreApi(`${basePath(skillId)}/unpublish`),
    });

export const republishAliceAgentApp = (skillId: string) =>
    callApi<void>({
        method: 'POST',
        url: routes.app.appStoreApi(`${basePath(skillId)}/republish`),
    });

/**
 * Probes the MCP server for the given draft AND records the connection fingerprint on
 * success (sets `manifest.config.mcp.verified=true` on the draft). Tools/resources/publish are
 * gated on this flag — any subsequent change to connection fields resets it server-side.
 */
export const observeMcp = (skillId: string, request: McpObserveRequest) =>
    callApi<McpObserveResponse>({
        method: 'POST',
        url: routes.app.appStoreApi(`${basePath(skillId)}/observe-mcp`),
        data: request,
    });

export interface AliceAgentAppImageDTO {
    id: string;
    url: string;
}

export const uploadAliceAgentAppLogo = (skillId: string, file: File) => {
    const data = new FormData();
    data.append('file', file);
    return callApi<AliceAgentAppImageDTO>({
        method: 'POST',
        url: routes.app.appStoreApi(`${basePath(skillId)}/logo`),
        data,
    });
};

const uaBase = (skillId: string) => `${basePath(skillId)}/user-agreements`;

export const getAliceAgentAppUserAgreements = (skillId: string, isDraft: boolean) =>
    callApi<UserAgreement[]>({
        method: 'GET',
        url: routes.app.appStoreApi(`${uaBase(skillId)}${isDraft ? '/drafts' : ''}`),
    });

export const createAliceAgentAppUserAgreement = (skillId: string, data: Pick<UserAgreementOrDraft, 'name' | 'url'>) =>
    callApi<UserAgreement>({
        method: 'POST',
        url: routes.app.appStoreApi(`${uaBase(skillId)}/drafts`),
        data,
    });

export const updateAliceAgentAppUserAgreement = (
    skillId: string,
    agreementId: string,
    data: Pick<UserAgreementOrDraft, 'name' | 'url'>,
) =>
    callApi<UserAgreement>({
        method: 'PATCH',
        url: routes.app.appStoreApi(`${uaBase(skillId)}/drafts/${agreementId}`),
        data,
    });

export const deleteAliceAgentAppUserAgreement = (skillId: string, agreementId: string) =>
    callApi<string>({
        method: 'DELETE',
        url: routes.app.appStoreApi(`${uaBase(skillId)}/drafts/${agreementId}`),
    });

export const reorderAliceAgentAppUserAgreements = (skillId: string, newOrder: Record<string, number>) =>
    callApi<string>({
        method: 'POST',
        url: routes.app.appStoreApi(`${uaBase(skillId)}/drafts/reorder`),
        data: newOrder,
    });
